export const GA_ID = process.env.REACT_APP_GA4_ID;

// public/index.html ships only the gtag command queue + Consent Mode defaults (everything denied — no cookies, no
// identifiers); gtag.js itself is injected by the template's loader on first interaction or ~3 s after load. Every event
// below goes through gtag('event', …), so Consent Mode decides what is actually sent. Accepting the banner flips consent
// to granted.
export function initGA() {
  if (!GA_ID || window.__gaInit) return;
  window.__gaInit = true;
  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);
    window.gtag("js", new Date());
    window.gtag("config", GA_ID, { send_page_view: false });
  }
  if (window.__loadGtag) window.__loadGtag();
  window.gtag("consent", "update", { ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted", analytics_storage: "granted" });
}

export function trackPageView(path) {
  if (window.gtag && GA_ID) window.gtag("event", "page_view", { page_path: path, page_location: window.location.href, page_title: document.title });
}

export function trackEvent(name, params = {}) {
  if (window.gtag && GA_ID) window.gtag("event", name, params);
}

// Conversion events (SEO batch 4) fired from ONE delegated click listener, so no link ever reports twice:
//   phone_click        any tel: link      · location = data-location on the link (header, sticky_bar, menu, footer) or "page"
//   email_click        any mailto: link   · location likewise
//   cta_free_visit_click  any link marked data-cta="free-visit" · location likewise
export function installClickTracking() {
  const onClick = (e) => {
    const a = e.target.closest && e.target.closest("a[href]");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    const location = a.dataset.location || "page";
    if (href.startsWith("tel:")) trackEvent("phone_click", { location, phone_number: href.replace("tel:", "") });
    else if (href.startsWith("mailto:")) trackEvent("email_click", { location });
    else if (a.dataset.cta === "free-visit") trackEvent("cta_free_visit_click", { location });
  };
  document.addEventListener("click", onClick);
  return () => document.removeEventListener("click", onClick);
}

// generate_lead — once per successful form: form ∈ contact | estimate | design_studio (+ job_application, bid_request).
export const trackLead = (form, { project_type = "", zip = "" } = {}, extra = {}) =>
  trackEvent("generate_lead", { form, project_type: project_type || "(not set)", zip: zip || "(not set)", ...extra });

// A 5-digit ZIP out of free text (an address field), or "".
export const zipFrom = (text = "") => (String(text).match(/\b\d{5}\b/) || [""])[0];

// Phase 5 results-page events: every one carries the project type, state and the displayed low / high.
export const trackEstimateEvent = (name, est, extra = {}) =>
  trackEvent(name, { project_type_key: est?.project_type_key, state: est?.state, low: est?.low, high: est?.high, ...extra });

export const CONSENT_KEY = "idb_cookie_consent";
export const hasAnalyticsConsent = () => { try { return localStorage.getItem(CONSENT_KEY) === "accepted"; } catch { return false; } };

// Lender pre-qual clicks: financing_click · lender = acorn | hfs. Consent Mode (gtag) decides whether it is sent.
export function trackFinancingClick(lender, placement, loanAmount) {
  const amount = Math.round(Number(loanAmount));
  trackEvent("financing_click", { lender, placement, page_path: window.location.pathname, ...(Number.isFinite(amount) && amount > 0 ? { loan_amount: amount } : {}) });
}

// Google Ads phone-call conversion. Navigates to `url` once gtag calls back, or after 1s as a fallback. Before gtag.js has
// arrived the event is only queued (no callback ever comes), so navigate at once — the queue replays it on load.
export function reportPhoneCallConversion(url) {
  if (typeof window.gtag !== "function" || !window.__gtagLoaded) { // link-audit: ok — completes a clicked tel: href
    if (typeof window.gtag === "function") window.gtag("event", "conversion", { send_to: "AW-11447868614/wxqACNuRqoMZEMa54tIq" });
    if (url) window.location.href = url; // link-audit: ok — completes a clicked tel: href
    return;
  }
  let done = false;
  const go = () => { if (done) return; done = true; if (url) window.location.href = url; }; // link-audit: ok — completes a clicked tel: href
  window.gtag("event", "conversion", {
    send_to: "AW-11447868614/wxqACNuRqoMZEMa54tIq",
    event_callback: go,
  });
  setTimeout(go, 1000);
}
